import { apiRequest } from "@/lib/api";
import type { EnrollmentListRow } from "@/lib/api/enrollments";
import type { RevenueAnalyticsResponse } from "@/lib/api/analytics";
import type { BookOrder } from "@/types/bookstore";

type DashboardQuery = {
  startDate?: string;
  endDate?: string;
};

function buildDateQuery(params?: DashboardQuery): string {
  if (!params?.startDate && !params?.endDate) return "";
  const qs = new URLSearchParams();
  if (params.startDate) qs.set("startDate", params.startDate);
  if (params.endDate) qs.set("endDate", params.endDate);
  const s = qs.toString();
  return s ? `?${s}` : "";
}

export type DashboardOverviewData = {
  revenue: {
    netSales: number;
    netIncome: number;
    totalRefunds: number;
    totalTransactions: number;
    successfulTransactions: number;
    failedTransactions: number;
    revenueChangePct: number | null;
  };
  enrollments: {
    enrolledToday: number;
    enrolledThisWeek: number;
    totalMatching: number;
  };
  upcoming: {
    totalUpcoming: number;
    totalAmount: number;
    next7Days: { count: number; amount: number };
  };
  tickets: {
    statusStats: Array<{ _id: string; count: number }>;
    categoryStats: Array<{ _id: string; count: number }>;
    priorityStats: Array<{ _id: string; count: number }>;
    recentTickets: number;
    totalTickets: number;
    weekOverWeekPct: number;
  };
  documents: {
    pending: number;
    uploaded: number;
    rejected: number;
  };
  notifications: {
    unread: number;
    urgent: number;
    high: number;
  };
  alerts: Array<{
    type: string;
    severity: string;
    message: string;
    timestamp?: string;
  }>;
};

export type DashboardRevenueData = {
  revenue: RevenueAnalyticsResponse["data"];
  activity: {
    alerts?: DashboardOverviewData["alerts"];
    metrics?: Record<string, unknown>;
    recentPayments?: unknown[];
  };
  upcoming: {
    totalUpcoming: number;
    totalAmount: number;
    next7Days: { count: number; amount: number };
    next30Days: { count: number; amount: number };
    next90Days?: { count: number; amount: number };
    thisMonth?: { count: number; amount: number };
    nextMonth?: { count: number; amount: number };
  };
  planStats: Array<{
    squarePlanId: string;
    enrolledCount: number;
    displayName: string | null;
    amount: number | null;
    planType: string | null;
    cadence: string | null;
    numberOfPayments: number | null;
    status: string | null;
    inCatalog: boolean;
  }>;
};

export type DashboardStudentPreview = {
  _id: string;
  email?: string;
  fullName?: string;
  firstName?: string;
  lastName?: string;
  isBlocked?: boolean;
};

export type DashboardProgramPreview = {
  _id: string;
  title?: string;
  status?: string;
};

export type DashboardEnrollmentsData = {
  summary: DashboardOverviewData["enrollments"];
  recentEnrollments: EnrollmentListRow[];
  enrollmentTotal: number;
  students: DashboardStudentPreview[];
  studentTotal: number;
  programs: DashboardProgramPreview[];
  programTotal: number;
};

export type DashboardOperationsData = {
  documents: {
    overall: {
      total: number;
      pending: number;
      uploaded: number;
      approved: number;
      rejected: number;
    };
    byType: Array<{
      documentType: string;
      total: number;
      pending: number;
      uploaded: number;
      approved: number;
      rejected: number;
      label: string;
    }>;
  };
  transcriptCounts: Record<string, number>;
  nameChangeCounts: Record<string, number>;
};

export type DashboardSupportData = {
  tickets: DashboardOverviewData["tickets"];
  leads: {
    total: number;
    byStatus: Record<string, number>;
  };
};

export type DashboardBookstoreData = {
  orders: BookOrder[];
  total: number;
  pendingFulfillment: number;
  revenuePreview: number;
};

export const dashboardApi = {
  getOverview(params?: DashboardQuery) {
    return apiRequest<{ data: DashboardOverviewData }>(
      `/api/admin/dashboard/overview${buildDateQuery(params)}`
    );
  },

  getRevenue(params?: DashboardQuery) {
    return apiRequest<{ data: DashboardRevenueData }>(
      `/api/admin/dashboard/revenue${buildDateQuery(params)}`
    );
  },

  getEnrollments() {
    return apiRequest<{ data: DashboardEnrollmentsData }>(
      "/api/admin/dashboard/enrollments"
    );
  },

  getOperations() {
    return apiRequest<{ data: DashboardOperationsData }>(
      "/api/admin/dashboard/operations"
    );
  },

  getSupport() {
    return apiRequest<{ data: DashboardSupportData }>(
      "/api/admin/dashboard/support"
    );
  },

  getBookstore() {
    return apiRequest<{ data: DashboardBookstoreData }>(
      "/api/admin/dashboard/bookstore"
    );
  },
};
